<template>
  <popup-block
    :closeModal="closeModal"
    :shown="opened"
    :loader="loading"
    :head_status_text="errorText"
    :header_status_error="true">
    <div class="acquisitions-apply">
      <template v-if="!submitted">
        <h2 class="acquisitions-apply__title">Apply as a {{ roleLabel }}</h2>
        <div class="acquisitions-apply__divider"/>

        <form class="acquisitions-apply__form" @submit.prevent="submit">
          <div class="acquisitions-apply__field">
            <label class="acquisitions-apply__label">
              Your full name (Latin characters)<span class="acquisitions-apply__required">*</span>
            </label>
            <input
              v-model="form.name"
              class="default-input"
              type="text"
              placeholder="e.g. John Smith"
              required>
          </div>

          <div class="acquisitions-apply__row">
            <div class="acquisitions-apply__field">
              <label class="acquisitions-apply__label">
                Your best email address<span class="acquisitions-apply__required">*</span>
              </label>
              <input
                v-model="form.email"
                class="default-input"
                type="email"
                pattern="^[^@\s]+@[^@\s]+\.[^@\s]{2,}$"
                title="Please enter a full email address, e.g. john.smith@email.com"
                placeholder="e.g. john.smith@email.com"
                required>
            </div>
            <div class="acquisitions-apply__field">
              <label class="acquisitions-apply__label">
                Your best phone number<span class="acquisitions-apply__required">*</span>
              </label>
              <phone-input v-model="form.phone"/>
            </div>
          </div>

          <div class="acquisitions-apply__field">
            <label class="acquisitions-apply__label">
              Choose your office:<span class="acquisitions-apply__required">*</span>
            </label>
            <default-select
              as_input_type
              dropdown_position_absolute
              :list_outer="offices"
              :selected_val="form.office"
              :empty_value_select_text="'Select your office'"
              @change="selectOffice"/>
          </div>

          <div class="acquisitions-apply__field">
            <label class="acquisitions-apply__label">
              Choose the languages you speak:<span class="acquisitions-apply__required">*</span>
            </label>
            <default-select
              as_input_type
              multiple
              dropdown_position_absolute
              :list_outer="languageOptions"
              :selected_val="form.languages"
              :empty_value_select_text="'Select your language(s)'"
              @change="val => form.languages = val"/>
          </div>

          <div class="acquisitions-apply__field">
            <label class="acquisitions-apply__label">
              The job is on-site, do you want to continue?<span class="acquisitions-apply__required">*</span>
            </label>
            <div class="acquisitions-apply__onsite">
              <custom-checkbox
                is_radio
                name="acquisitions_apply_onsite"
                :checked="onsiteChoice === 'yes'"
                @change="onsiteChoice = 'yes'">
                Yes, I want to apply
              </custom-checkbox>
              <custom-checkbox
                is_radio
                name="acquisitions_apply_onsite"
                :checked="onsiteChoice === 'no'"
                @change="onsiteChoice = 'no'">
                I'm not interested
              </custom-checkbox>
            </div>
          </div>

          <terms-checkbox v-model="agreed"/>

          <default-button
            type="submit"
            class="acquisitions-apply__submit"
            :disabled="submitDisabled">
            {{ loading ? 'Submitting…' : 'Apply Now' }}
          </default-button>
        </form>
      </template>

      <template v-else>
        <div class="acquisitions-apply__success">
          <div class="acquisitions-apply__success-icon">
            <svg width="24" height="18" viewBox="0 0 40 30" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M36.334 4.16666L14.6673 25.8333L3.83398 15" stroke="#fff" stroke-width="7" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h2 class="acquisitions-apply__success-title">Application Submitted!</h2>
          <p class="acquisitions-apply__success-text">
            Thank you {{ formattedName }} for applying. We will carefully review your details
            and get back to you via email with the next steps.
          </p>
          <div class="acquisitions-apply__summary">
            <div class="acquisitions-apply__summary-heading">Submission Summary</div>
            <div class="acquisitions-apply__summary-row">
              <span>Selected Office:</span>
              <strong>{{ selectedOfficeLabel }}</strong>
            </div>
            <div class="acquisitions-apply__summary-row">
              <span>Languages:</span>
              <strong>{{ selectedLanguagesLabel }}</strong>
            </div>
            <div class="acquisitions-apply__summary-row">
              <span>Application Type:</span>
              <strong>{{ roleLabel }} (On-Site)</strong>
            </div>
          </div>
          <default-button class="acquisitions-apply__submit" @click="closeModal">
            Got It, Thanks!
          </default-button>
        </div>
      </template>
    </div>
  </popup-block>
</template>

<script>
import PopupBlock from "../modules/PopupBlock.vue";
import PhoneInput from "../small_parts/PhoneInput.vue";
import TermsCheckbox from "../small_parts/TermsCheckbox.vue";
import DefaultSelect from "../small_parts/DefaultSelect.vue";
import DefaultButton from "../slots/DefaultButton.vue";
import CustomCheckbox from "../small_parts/CustomCheckbox.vue";
import {axiosAcquisitionsApply} from "../../helpers/axios_requests";
import {getUtmAttribution} from "../../helpers/utm_attribution";

// Language options are free-text labels configured per office in the admin
// (RoleCardsBlock's `offices` prop) — e.g. "Polish + English" is one
// selectable option. Typos are possible since nothing validates against a
// catalog; accepted tradeoff. splitLanguageLabel() below is what turns a
// selected label back into discrete values for the outgoing payload.
function splitLanguageLabel(label) {
  return (label || '')
    .split('+')
    .map(part => part.trim())
    .filter(Boolean)
}

function flattenSelectedLanguages(selected) {
  const values = new Set()
  selected.forEach(({title}) => {
    splitLanguageLabel(title).forEach(value => values.add(value))
  })
  return [...values]
}

function toTitleCase(value) {
  return (value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/(^|[\s-])\p{L}/gu, char => char.toUpperCase())
}

const NEW_FORM = () => ({
  name: '',
  email: '',
  phone: '',
  office: null,
  languages: [],
})

export default {
  name: 'AcquisitionsApplyModal',
  components: {CustomCheckbox, DefaultButton, DefaultSelect, TermsCheckbox, PhoneInput, PopupBlock},
  props: {
    offices: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      opened: false,
      role: 'buyer',
      form: NEW_FORM(),
      onsiteChoice: 'yes',
      agreed: false,
      submitted: false,
      loading: false,
      errorText: '',
    }
  },
  computed: {
    roleLabel() {
      return this.role === 'prospector' ? 'Prospector' : 'Buyer'
    },
    formattedName() {
      return toTitleCase(this.form.name)
    },
    languageOptions() {
      const languages = this.form.office?.languages || []
      return languages
        .map(({label}) => (label || '').trim())
        .filter(Boolean)
        .map(label => ({id: label, title: label}))
    },
    selectedOfficeLabel() {
      return this.form.office ? `${this.form.office.title}, Romania` : ''
    },
    selectedLanguagesLabel() {
      return this.form.languages.map(l => l.title).join(', ')
    },
    submitDisabled() {
      return this.loading
        || this.onsiteChoice !== 'yes'
        || !this.agreed
        || !this.form.name
        || !this.form.email
        || !this.form.phone
        || !this.form.office
        || !this.form.languages.length
    },
  },
  methods: {
    openModal(role) {
      this.role = role === 'prospector' ? 'prospector' : 'buyer'
      this.form = NEW_FORM()
      this.onsiteChoice = 'yes'
      this.agreed = false
      this.submitted = false
      this.errorText = ''
      this.opened = true
    },
    closeModal() {
      this.opened = false
      this.$emit('closed', true)
    },
    selectOffice(item) {
      this.form.office = item
      // The language list depends on the office — whatever was picked before
      // may not even exist for the new one.
      this.form.languages = []
    },
    async submit() {
      if (this.submitDisabled) return
      this.loading = true
      this.errorText = ''
      const attribution = getUtmAttribution()
      try {
        await axiosAcquisitionsApply.send({
          name: this.formattedName,
          email: this.form.email,
          phone: this.form.phone,
          role: this.role,
          office: this.form.office.id,
          languages: flattenSelectedLanguages(this.form.languages),
          onsite_confirmed: true,
          ...attribution,
        })
        this.submitted = true
      } catch (e) {
        this.errorText = 'Something went wrong — please try again in a moment.'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
